import React from "react";
import { motion } from "framer-motion";
import { Truck, Ship, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/shared/SectionHeading";

const RED_TRUCK_IMG = "/images/camion-rouge-preview.png";

const services = [
  {
    icon: <Truck size={28} />,
    color: "text-[#E8902A]",
    bg: "bg-[#E8902A]/10 group-hover:bg-[#E8902A]/20",
    title: "Transport Routier",
    desc: "Expéditions nationales et européennes pour tous types de marchandises et tout conditionnement.",
    features: ["Lots complets", "Groupage", "Express"],
    img: RED_TRUCK_IMG,
    span: "md:col-span-1",
    href: "/services#transport-routier",
  },
  {
    icon: <Ship size={28} />,
    color: "text-[#E8902A]",
    bg: "bg-[#E8902A]/10 group-hover:bg-[#E8902A]/20",
    title: "Fret Maritime",
    desc: "Du conteneur de groupage au conteneur complet, nous vous proposons les meilleures solutions de transport maritime à travers le monde.",
    features: ["FCL & LCL", "Tous ports mondiaux", "Door to Door"],
    img: "/images/camion-routier.jpeg",
    span: "md:col-span-1",
    href: "/services#fret-maritime",
  },
];

export default function ServicesPreview() {
  return (
    <section className="py-24 bg-[#1c3048]" data-testid="services-preview">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          label="Nos expertises"
          title={<>Solutions <span className="gradient-text">sur mesure</span> pour chaque besoin</>}
          description="Du transport routier régional et européen au fret maritime intercontinental, nous couvrons toute votre chaîne logistique."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`${service.span} group relative rounded-2xl overflow-hidden border border-white/10 bg-[#243d56]/50 hover:bg-[#2a4760]/70 transition-all duration-500 cursor-pointer`}
              data-testid={`service-card-${i}`}
            >
              <div className="absolute inset-0">
                <img
                  src={service.img}
                  alt={service.title}
                  className="w-full h-full object-cover opacity-55 group-hover:opacity-70 transition-all duration-700 scale-105 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1825]/90 via-[#1a2e48]/55 to-[#1a2e48]/20" />
              </div>

              <div className="relative p-7 h-full flex flex-col justify-between min-h-[260px]">
                <div>
                  <div className={`w-12 h-12 rounded-xl ${service.bg} ${service.color} flex items-center justify-center mb-5 transition-colors duration-300`}>
                    {service.icon}
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-white mb-3" style={{textShadow:"0 2px 8px rgba(0,0,0,0.7)"}}>{service.title}</h3>
                  <p className="text-white text-base leading-relaxed mb-5" style={{textShadow:"0 1px 6px rgba(0,0,0,0.8)"}}>{service.desc}</p>
                  <ul className="space-y-2">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-white text-sm font-semibold" style={{textShadow:"0 1px 4px rgba(0,0,0,0.7)"}}>
                        <div className={`w-1.5 h-1.5 rounded-full bg-[#E8902A] flex-shrink-0`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to={service.href} className="mt-6 flex items-center gap-2 text-[#E8902A] text-sm font-heading font-semibold group-hover:gap-3 transition-all duration-300" style={{textShadow:"0 1px 4px rgba(0,0,0,0.6)"}}>
                  En savoir plus <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-8 text-center"
        >
          <Link to="/services" data-testid="see-all-services-btn">
            <button className="flex items-center gap-2 mx-auto border border-white/15 hover:border-[#E8902A]/40 text-slate-300 hover:text-white font-heading font-semibold text-sm tracking-widest uppercase px-8 py-3 transition-all duration-300 rounded-xl hover:bg-[#E8902A]/5">
              Voir tous nos services <ArrowRight size={16} />
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
